import React from 'react';
import { ArrowDown, ArrowUp, CopyPlus, Pencil, Trash2 } from 'lucide-react';

import CodeChip from './CodeChip';
import { ExpiringChip, StatusChip, TypeChip, UsageMeter } from './CouponMeta';
import { formatDateRange } from './couponAdapter';
import { formatCouponAmount, getCouponStatus } from './couponStatus';
import { MiniLoader } from '../../reuse/SVG';

/** Desktop layout. Below `md` the page renders CouponCardList instead. */

const Header = ({ label, sortKey, align = 'left', sort, onSort }) => {
    const isActive = sortKey !== undefined && sort.key === sortKey;
    const alignClass = { left: 'cpn-text-left', center: 'cpn-text-center', right: 'cpn-text-right' }[align];
    const base = `cpn-px-6 cpn-py-3.5 cpn-text-xs cpn-font-bold cpn-uppercase cpn-tracking-wider cpn-text-neutral-500 ${alignClass}`;

    if (!sortKey) {
        return (
            <th scope="col" className={base}>
                {label}
            </th>
        );
    }

    return (
        <th
            scope="col"
            className={base}
            aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                // cpn-p-0: the page's button reset leaves the UA's ~6px side padding in place, which
                // pushed sortable labels off their column's edge while plain ones (Type) sat flush.
                className={`cpn-inline-flex cpn-items-center cpn-gap-1 cpn-p-0 cpn-uppercase cpn-tracking-wider cpn-transition hover:cpn-text-brand-700 ${
                    isActive ? 'cpn-text-brand-700' : ''
                } ${align === 'right' ? 'cpn-flex-row-reverse' : ''}`}
            >
                {label}
                {isActive &&
                    (sort.direction === 'asc' ? (
                        <ArrowUp className="cpn-h-3 cpn-w-3" aria-hidden="true" />
                    ) : (
                        <ArrowDown className="cpn-h-3 cpn-w-3" aria-hidden="true" />
                    ))}
            </button>
        </th>
    );
};

/**
 * Native checkbox, not the prototype's `rounded border-neutral-300 text-brand-700` classes: those
 * only do anything with `@tailwindcss/forms`, and `tailwind.coupons.config.js` has `plugins: []`,
 * so they compile to nothing a checkbox can use. `accent-*` brands the native control directly.
 */
export const CHECKBOX_CLASS = 'cpn-h-4 cpn-w-4 cpn-cursor-pointer cpn-accent-brand-700';

const CouponTable = ({
    coupons,
    sort,
    highlightId,
    togglingId,
    selectedIds,
    onSort,
    onSelect,
    onSelectPage,
    onOpen,
    onClone,
    onToggle,
    onDelete,
    onCopy,
}) => {
    const allSelected = coupons.length > 0 && coupons.every(coupon => selectedIds.includes(coupon.id));
    const someSelected = !allSelected && coupons.some(coupon => selectedIds.includes(coupon.id));

    return (
    <div className="cpn-hidden cpn-overflow-x-auto md:cpn-block">
        <table className="cpn-w-full cpn-min-w-[980px] cpn-border-collapse cpn-text-sm">
            <caption className="cpn-sr-only">Coupon codes for this camp</caption>
            <thead>
                <tr className="cpn-border-b cpn-border-neutral-200 cpn-bg-neutral-50">
                    <th scope="col" className="cpn-w-12 cpn-px-4 cpn-py-3.5">
                        <input
                            type="checkbox"
                            checked={allSelected}
                            // `indeterminate` is a property, not an attribute — React cannot set it
                            // through JSX, so it has to be assigned on the node itself.
                            ref={element => {
                                if (element) element.indeterminate = someSelected;
                            }}
                            onChange={event => onSelectPage(event.target.checked)}
                            aria-label="Select all coupons on this page"
                            className={CHECKBOX_CLASS}
                        />
                    </th>
                    <Header label="Coupon" sortKey="name" sort={sort} onSort={onSort} />
                    <Header label="Status" sortKey="status" sort={sort} onSort={onSort} />
                    <Header label="Redeemed" sortKey="redeemed" sort={sort} onSort={onSort} />
                    <Header label="Type" align="center" sort={sort} onSort={onSort} />
                    <Header label="Value" sortKey="value" align="center" sort={sort} onSort={onSort} />
                    <Header label="Actions" align="right" sort={sort} onSort={onSort} />
                </tr>
            </thead>
            <tbody className="cpn-divide-y cpn-divide-neutral-100">
                {coupons.map(coupon => {
                    const selected = selectedIds.includes(coupon.id);
                    return (
                    <tr
                        key={coupon.id}
                        onClick={() => onOpen(coupon)}
                        className={`cpn-cursor-pointer cpn-align-top cpn-transition ${
                            highlightId === coupon.id
                                ? 'cpn-bg-brand-50 cpn-ring-2 cpn-ring-inset cpn-ring-brand-400'
                                : selected
                                ? 'cpn-bg-brand-50/60'
                                : 'hover:cpn-bg-brand-50/40'
                        }`}
                    >
                        {/* stopPropagation because the row's own onClick opens the modal — without
                            it, ticking a box would also open the coupon behind the bulk bar. */}
                        <td className="cpn-px-4 cpn-py-4" onClick={event => event.stopPropagation()}>
                            <input
                                type="checkbox"
                                checked={selected}
                                onChange={event => onSelect(coupon.id, event.target.checked)}
                                aria-label={`Select ${coupon.name}`}
                                className={CHECKBOX_CLASS}
                            />
                        </td>

                        <td className="cpn-px-6 cpn-py-4">
                            <button
                                type="button"
                                onClick={event => {
                                    event.stopPropagation();
                                    onOpen(coupon);
                                }}
                                className="cpn-p-0 cpn-text-left cpn-font-semibold cpn-text-neutral-900 cpn-transition hover:cpn-text-brand-700 hover:cpn-underline"
                            >
                                {coupon.name}
                            </button>
                            <div className="cpn-mt-1.5 cpn-flex cpn-flex-wrap cpn-items-center cpn-gap-2">
                                <CodeChip code={coupon.code} onCopied={onCopy} />
                                <span className="cpn-text-xs cpn-text-neutral-500">
                                    {formatDateRange(coupon.startDate, coupon.endDate)}
                                </span>
                            </div>
                        </td>

                        <td className="cpn-px-6 cpn-py-4">
                            <div className="cpn-flex cpn-flex-col cpn-items-start cpn-gap-1.5">
                                <StatusChip status={getCouponStatus(coupon)} />
                                <ExpiringChip coupon={coupon} />
                            </div>
                        </td>

                        <td className="cpn-px-6 cpn-py-4">
                            <UsageMeter coupon={coupon} />
                        </td>

                        <td className="cpn-px-6 cpn-py-4 cpn-text-center">
                            <TypeChip type={coupon.type} />
                        </td>

                        <td className="cpn-px-6 cpn-py-4 cpn-text-center cpn-font-semibold cpn-tabular-nums cpn-text-neutral-900">
                            {formatCouponAmount(coupon)}
                        </td>

                        <td className="cpn-px-6 cpn-py-4" onClick={event => event.stopPropagation()}>
                            <div className="cpn-flex cpn-items-center cpn-justify-end cpn-gap-1.5">
                                {togglingId === coupon.id ? (
                                    <span className="coupon_toggle_loader cpn-mr-1 cpn-flex cpn-h-6 cpn-w-11 cpn-items-center cpn-justify-center">
                                        <MiniLoader />
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={coupon.active}
                                        aria-label={`${coupon.active ? 'Deactivate' : 'Activate'} ${
                                            coupon.name
                                        }`}
                                        onClick={() => onToggle(coupon)}
                                        className={`cpn-relative cpn-mr-1 cpn-h-6 cpn-w-11 cpn-rounded-full cpn-transition focus:cpn-outline-none focus-visible:cpn-ring-2 focus-visible:cpn-ring-brand-300 focus-visible:cpn-ring-offset-2 ${
                                            coupon.active ? 'cpn-bg-emerald-500' : 'cpn-bg-neutral-300'
                                        }`}
                                    >
                                        <span
                                            className={`cpn-absolute cpn-top-0.5 cpn-h-5 cpn-w-5 cpn-rounded-full cpn-bg-white cpn-shadow-sm cpn-transition-all ${
                                                coupon.active ? 'cpn-left-[22px]' : 'cpn-left-0.5'
                                            }`}
                                        />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onOpen(coupon)}
                                    aria-label={`Edit ${coupon.name}`}
                                    title="Edit"
                                    className="cpn-rounded-lg cpn-p-2 cpn-text-neutral-400 cpn-transition hover:cpn-bg-brand-50 hover:cpn-text-brand-700 focus:cpn-outline-none focus-visible:cpn-ring-2 focus-visible:cpn-ring-brand-200"
                                >
                                    <Pencil className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                                </button>
                                {/* CopyPlus, not Copy: CodeChip already uses Copy for
                                    copy-to-clipboard, and two identical icons in one row would read
                                    as the same action. */}
                                <button
                                    type="button"
                                    onClick={() => onClone(coupon)}
                                    aria-label={`Clone ${coupon.name}`}
                                    title="Clone"
                                    className="cpn-rounded-lg cpn-p-2 cpn-text-neutral-400 cpn-transition hover:cpn-bg-brand-50 hover:cpn-text-brand-700 focus:cpn-outline-none focus-visible:cpn-ring-2 focus-visible:cpn-ring-brand-200"
                                >
                                    <CopyPlus className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => onDelete(coupon)}
                                    aria-label={`Delete ${coupon.name}`}
                                    title="Delete"
                                    className="cpn-rounded-lg cpn-p-2 cpn-text-neutral-400 cpn-transition hover:cpn-bg-red-50 hover:cpn-text-red-600 focus:cpn-outline-none focus-visible:cpn-ring-2 focus-visible:cpn-ring-red-200"
                                >
                                    <Trash2 className="cpn-h-4 cpn-w-4" aria-hidden="true" />
                                </button>
                            </div>
                        </td>
                    </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
    );
};

export default CouponTable;
